import {
  AdsAccount,
  AdsCampaign,
  AdsGroup,
  AdsGroupAd,
} from '@betterfunnel/types';
import { collection, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';

export const adsAccountsColRef = (accountId: string) => {
  return collection(db, 'accounts', accountId, 'adsAccounts').withConverter(
    emptyConverter<AdsAccount>(),
  );
};

export const activeAdsAccountsQuery = (accountId: string) => {
  return query(adsAccountsColRef(accountId), where('status', '==', 'active'));
};

export const approveConnectRequest = async (
  accountId: string,
  requestId: string,
  adsAccountsIds: string[],
) => {
  const fn = httpsCallable(
    functions,
    'accounts-adsAccounts-approveConnectRequest',
  );
  const res = await fn({ accountId, requestId, adsAccountsIds });

  return res.data;
};

export const resyncAdsAccount = async (
  accountId: string,
  adsAccountsIds: string[],
) => {
  const fn = httpsCallable(functions, 'accounts-adsAccounts-resync');
  const res = await fn({ accountId, adsAccountsIds });

  return res.data;
};

export const getCampaigns = async (
  accountId: string,
  fromDate: string,
  toDate: string,
  providers: string[] = ['google'],
  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[],
): Promise<AdsCampaign[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      providers: string[];
      fromDate: string;
      toDate: string;
      filters?: {
        campaignId?: string;
        adsAccountId: string;
        provider: 'google' | 'facebook';
      }[];
    },
    AdsCampaign[]
  >(functions, 'accounts-adsAccounts-getCampaigns');
  const res = await fn({
    accountId,
    providers,
    fromDate,
    toDate,
    ...(filters ? { filters } : {}),
  });

  return res.data;
};

export const getAdGroups = async (
  accountId: string,
  fromDate: Date,
  toDate: Date,
  provider: string,
  adsAccountId: string,
  campaignIds: string[],
): Promise<AdsGroup[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      provider: string;
      adsAccountId: string;
      fromDate: Date;
      toDate: Date;
      campaignIds: string[];
    },
    AdsGroup[]
  >(functions, 'accounts-adsAccounts-getAdGroups');
  const res = await fn({
    accountId,
    provider,
    adsAccountId,
    fromDate,
    toDate,
    campaignIds,
  });

  return res.data;
};

export const getAdGroupAds = async (
  accountId: string,
  fromDate: Date,
  toDate: Date,
  provider: string,
  adsAccountId: string,
  campaignIds: string[],
  adGroupIds: string[],
): Promise<AdsGroupAd[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      provider: string;
      adsAccountId: string;
      fromDate: Date;
      toDate: Date;
      campaignIds: string[];
      adGroupIds: string[];
    },
    AdsGroupAd[]
  >(functions, 'accounts-adsAccounts-getAdGroupAds');
  const res = await fn({
    accountId,
    provider,
    adsAccountId,
    fromDate,
    toDate,
    campaignIds,
    adGroupIds,
  });

  return res.data;
};

export const getCachedCampaigns = async (
  accountId: string,
  providers: string[] = ['google'],
  filters?: {
    campaignId?: string | string[];
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[],
  limit: number = 1000,
  offset: number = 0,
): Promise<AdsCampaign[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      providers: string[];
      filters?: {
        campaignId?: string | string[];
        adsAccountId: string;
        provider: 'google' | 'facebook';
      }[];
      limit: number;
      offset: number;
    },
    AdsCampaign[]
  >(functions, 'accounts-adsAccounts-getCachedCampaigns');
  const res = await fn({
    accountId,
    providers,
    ...(filters ? { filters } : {}),
    limit,
    offset,
  });

  return res.data;
};

export const getCachedAdGroups = async (
  accountId: string,
  provider: 'google' | 'facebook',
  adsAccountId: string,
  campaignIds: string[],
  limit: number = 1000,
  offset: number = 0,
): Promise<AdsGroup[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      providers: ('google' | 'facebook')[];
      filters: {
        campaignId?: string | string[];
        adsAccountId: string;
        provider: 'google' | 'facebook';
      }[];
      limit: number;
      offset: number;
    },
    AdsGroup[]
  >(functions, 'accounts-adsAccounts-getCachedAdGroups');
  const res = await fn({
    accountId,
    providers: [provider],
    filters: [
      {
        provider,
        adsAccountId,
        ...(campaignIds ? { campaignId: campaignIds } : {}),
      },
    ],
    limit,
    offset,
  });

  return res.data;
};

export const getCachedAdGroupAds = async (
  accountId: string,
  provider: 'google' | 'facebook',
  adsAccountId: string,
  campaignIds: string[],
  adGroupIds: string[],
  limit: number = 1000,
  offset: number = 0,
): Promise<AdsGroupAd[]> => {
  const fn = httpsCallable<
    {
      accountId: string;
      providers: ('google' | 'facebook')[];
      filters: {
        campaignId?: string | string[];
        adGroupId?: string | string[];
        adsAccountId: string;
        provider: 'google' | 'facebook';
      }[];
      limit: number;
      offset: number;
    },
    AdsGroupAd[]
  >(functions, 'accounts-adsAccounts-getCachedAdGroupAds');
  const res = await fn({
    accountId,
    providers: [provider],
    filters: [
      {
        provider,
        ...(campaignIds ? { campaignId: campaignIds } : {}),
        ...(adGroupIds ? { adGroupId: adGroupIds } : {}),
        adsAccountId,
      },
    ],
    limit,
    offset,
  });

  return res.data;
};

export const analyzeAdsAccount = async (
  accountId: string,
  requestId: string,
  fromDate: Date,
  toDate: Date,
  adsAccountId: string,
  campaignIds?: string[],
): Promise<unknown> => {
  const fn = httpsCallable<{
    accountId: string;
    requestId: string;
    adsAccountId: string;
    fromDate: Date;
    toDate: Date;
    campaignIds?: string[];
  }>(functions, 'accounts-adsAccounts-analyze-analyzeAccount');

  const res = await fn({
    accountId,
    requestId,
    adsAccountId,
    fromDate,
    toDate,
    ...(campaignIds ? { campaignIds } : {}),
  });

  return res.data;
};
